import { Rank, type EnemyHookContext } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** E-I4 — ESCADRILLE. Le duo qui SE CROISE.
 *
 *  Deux chasseurs légers lancés en même temps, l'un du bord gauche, l'autre du
 *  droit. Chacun descend en OBLIQUE vers le côté opposé et lâche un tir visé de
 *  loin en loin. Leurs deux diagonales se coupent au centre : un ciseau qui se
 *  referme puis se rouvre.
 *
 *  ── Ce qu'elle enseigne ──
 *  À lire une GÉOMÉTRIE d'ensemble, pas deux menaces séparées. Pris un par un,
 *  chaque appareil est trivial — une ligne droite. Ensemble, ils dessinent un X
 *  mouvant dont le point de croisement est le seul endroit vraiment fermé, et
 *  pour un temps très court. Le joueur qui suit un appareil des yeux se fait
 *  cueillir par l'autre ; celui qui voit le ciseau sait qu'il y a tout l'espace
 *  du monde AVANT et APRÈS le croisement, et un instant à ne pas passer PENDANT.
 *  C'est la lecture spatiale d'une formation, préparation aux gerbes croisées
 *  des boss.
 *
 *  ── Pourquoi un hook, et pourquoi DEUX définitions ──
 *  Le moteur connaît la sinusoïde et la traversée horizontale, aucune ne donne
 *  cette OBLIQUE constante (descente + dérive latérale fixe). Un hook minuscule
 *  l'ajoute : une dérive de signe constant, ramenée à 40 % à l'apparition comme
 *  le reste (le moteur entre en douceur, le hook doit en faire autant).
 *
 *  Le sens de la dérive ne peut pas se déduire à chaque tick de la position
 *  courante : une fois le centre franchi, la lecture du signe s'inverserait et
 *  l'appareil ferait demi-tour. On FIXE donc le sens dans la définition — d'où
 *  deux archétypes, `escadrille-a` (dérive à droite) et `escadrille-b` (dérive à
 *  gauche) — que la vague dépose sur les bords OPPOSÉS pour obtenir le
 *  croisement. La fabrique `croiser` ne s'exécute qu'au chargement ; le hook
 *  qu'elle renvoie, lui, n'alloue rien. */

/** Vitesse de la dérive latérale, en u/s. Franche mais sous la vitesse du
 *  joueur : l'oblique se distance, le croisement s'anticipe. */
const DRIFT = 150;

function croiser(sens: number) {
  return (slot: number, age: number, dt: number, ctx: EnemyHookContext): void => {
    const ramp = age < 0.5 ? age * 2 : 1;
    ctx.x[slot] = ctx.x[slot]! + sens * DRIFT * dt * ramp;
  };
}

const base = {
  rank: Rank.Standard,
  hp: 60,
  visualRadius: 18,
  score: 120,
  telegraph: 0.7,
  drops: 1,
  // Descente droite au moteur (Linear) ; le hook n'ajoute QUE la dérive oblique.
  motion: { kind: Motion.Linear, speed: 240 },
  emitter: {
    // Un unique tir visé de loin en loin : la menace est la formation, pas le
    // rideau. Il rappelle juste que les appareils mordent.
    pattern: Pattern.Aimed,
    every: 1.6,
    count: 1,
    speed: 420,
    firstDelay: 1.1,
    family: Fam.EnemyPurple,
  },
} as const;

const escadrilleA = defineEnemy({
  ...base,
  id: 'escadrille-a',
  onUpdate: croiser(1),
});
const escadrilleB = defineEnemy({
  ...base,
  id: 'escadrille-b',
  onUpdate: croiser(-1),
});

export default [escadrilleA, escadrilleB];
